'use client';

/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { ProductInformation } from '@dealerportal/common';
import { useFavouriteProducts } from '@dealerportal/react-helpers';
import { type MouseEventHandler, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { MarkFavourite } from '../ProductCard/MarkFavourite';
import { MissingImage } from '../ProductCard/MissingImage';

interface SwatchZoomProps {
  src: string;
  productInfo: ProductInformation;
}

/**
 * Implements an image that you can zoom within on hover
 * For the product image on a PDP page
 */

export const SwatchZoom = (props: SwatchZoomProps) => {
  const zoomedDiv = useRef<HTMLDivElement>(null);
  const zoomedImage = useRef<HTMLImageElement>(null);

  const favouriteProductsCtx = useFavouriteProducts();
  const isFavourite = useMemo(
    () =>
      favouriteProductsCtx?.find({
        UPID: props?.productInfo?.UPID ?? '',
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favouriteProductsCtx?.favouriteProducts, props?.productInfo]
  );

  const mouseOverZoom: MouseEventHandler<HTMLDivElement> = (e) => {
    if (zoomedImage.current == null || zoomedDiv.current == null) return;
    const ratio =
      zoomedImage.current.naturalHeight / zoomedImage.current.naturalWidth;
    const rect = zoomedDiv.current.getBoundingClientRect();
    const xPos = e.clientX - rect.left;
    const yPos = e.clientY - rect.top;
    const xPercent = `${xPos / (zoomedDiv.current.clientWidth / 100)}%`;
    const yPercent = `${yPos / ((zoomedDiv.current.clientWidth * ratio) / 100)}%`;
    Object.assign(zoomedDiv.current.style, {
      backgroundPosition: `${xPercent} ${yPercent}`,
      backgroundSize: `${zoomedImage.current.naturalWidth}px`,
    });
  };

  const mouseLeaveZoom: MouseEventHandler<HTMLDivElement> = () => {
    if (zoomedDiv.current == null) return;
    Object.assign(zoomedDiv.current.style, {
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    });
  };

  const imageSrc = props.src?.includes('res.cloudinary.com')
    ? props.src.replace('https://images.floorforce.com/Textures/', '')
    : props.src;

  const handleFavouriteClick = () => {
    if (favouriteProductsCtx?.addRemoveProductsFromFav) {
      const wasFavourite = isFavourite;
      favouriteProductsCtx.addRemoveProductsFromFav(props.productInfo);

      if (!wasFavourite) {
        toast('Product added to favorites');
      }
    }
  };

  return (
    <div>
      {imageSrc && (
        <div
          ref={zoomedDiv}
          onMouseMove={mouseOverZoom}
          onMouseLeave={mouseLeaveZoom}
          style={{
            backgroundImage: `url(${imageSrc})`,
            height: '400px',
            width: '100%',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          title={`${props.productInfo.productName} by ${props.productInfo.brandName} - ${props.productInfo.colorName}`}
        />
      )}
      {!imageSrc && <MissingImage height={400} />}
      <MarkFavourite
        btnClass="!ui-absolute !ui-top-[2.5%] !ui-right-[2.5%] ui-size-[2.5rem]"
        iconClass="ui-size-[1.35rem]"
        isFavourite={isFavourite}
        onClick={handleFavouriteClick}
      />
      {imageSrc && (
        <img
          src={imageSrc}
          ref={zoomedImage}
          alt={`${props.productInfo.productName} by ${props.productInfo.brandName} - ${props.productInfo.colorName}`}
          height={448}
          width={400}
          style={{
            backgroundImage: `url(${imageSrc})`,
            height: '448px',
            width: '100%',
            display: 'none',
          }}
        />
      )}
    </div>
  );
};
